import React from 'react';
import { Head, Link, usePage } from '@inertiajs/react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '@/components/ui/button';

// Types
interface User {
  id: number;
  name?: string;
  username?: string;
  email: string;
  role: 'user' | 'admin' | 'manager';
  otp_required_for_login: boolean;
}

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  summary: string;
  publishedAt: string | null;
  visibility: 'visible' | 'auth_only';
  viewCount: number;
  readingTimeMinutes: number;
}

interface Newsletter {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  deliverySchedule: string;
  isSubscribed: boolean;
}

interface DashboardProps {
  user: User;
  publicPosts: BlogPost[];
  authPosts: BlogPost[];
  publicNewsletters: Newsletter[];
  authNewsletters: Newsletter[];
}

// Helper function to format date
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Premium Badge Component
function PremiumBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium px-2 py-1 rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      Premium
    </span>
  );
}

// Members Only Badge Component
function MembersOnlyBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium px-2 py-1 rounded-full">
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      Members
    </span>
  );
}

// Blog Post Card Component
function BlogPostCard({ post }: { post: BlogPost }) {
  const isAuthOnly = post.visibility === 'auth_only';
  
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="block bg-card dark:bg-card text-foreground rounded-lg border border-border p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex flex-wrap gap-2 mb-2">
        {isAuthOnly && <MembersOnlyBadge />}
      </div>
      <h3 className="text-base font-semibold text-foreground mb-2 hover:text-primary transition-colors line-clamp-2">
        {post.title}
      </h3>
      {post.summary && (
        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
          {post.summary}
        </p>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatDate(post.publishedAt)}</span>
        <span>{post.readingTimeMinutes} min read</span>
      </div>
    </Link>
  );
}

// Newsletter Card Component
function NewsletterCard({ newsletter }: { newsletter: Newsletter }) {
  const getScheduleText = (schedule: string) => {
    switch (schedule) {
      case 'daily':
        return 'Sent daily';
      case 'weekly':
        return 'Sent weekly';
      case 'monthly':
        return 'Sent monthly';
      default:
        return 'Sent manually';
    }
  };

  return (
    <div className="bg-card dark:bg-card text-foreground rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <Link
          href={`/newsletters/${newsletter.slug}`}
          className="text-base font-semibold text-foreground hover:text-primary transition-colors"
        >
          {newsletter.name}
        </Link>
        {newsletter.isSubscribed && (
          <span className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium px-2 py-1 rounded-full shrink-0">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Subscribed
          </span>
        )}
      </div>
      {newsletter.description && (
        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
          {newsletter.description}
        </p>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{getScheduleText(newsletter.deliverySchedule)}</span>
        <Link 
          href={`/newsletters/${newsletter.slug}`}
          className="text-primary hover:underline"
        >
          View →
        </Link>
      </div>
    </div>
  );
}

// Section Header Component
function SectionHeader({ 
  title, 
  description, 
  link 
}: { 
  title: string; 
  description?: string;
  link?: { href: string; text: string };
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {link && (
        <Link 
          href={link.href}
          className="text-sm text-primary hover:underline"
        >
          {link.text} →
        </Link>
      )}
    </div>
  );
}

// Empty State Component
function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8">
      <p className="text-muted-foreground text-sm">{message}</p>
    </div>
  );
}

export default function Dashboard({ 
  user, 
  publicPosts, 
  authPosts, 
  publicNewsletters, 
  authNewsletters 
}: DashboardProps) {
  return (
    <DashboardLayout>
      <Head title="Dashboard" />
      <div className="space-y-8">
        {/* Welcome Header */}
        <div>
          <h1 className="text-2xl font-bold">Welcome back, {user.name || 'there'}!</h1>
          <p className="text-muted-foreground">
            Here's what's new from the blog and newsletters.
          </p>
        </div>

        {/* Blog Posts Section - Public */}
        <section>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div>
              <h2 className="text-xl font-bold text-foreground">Latest Blog Posts</h2>
              <p className="text-sm text-muted-foreground">Public articles available to everyone</p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link href="/blog">View all posts</Link>
            </Button>
          </div>
          {publicPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publicPosts.map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <EmptyState message="No public posts available yet." />
          )}
        </section>

        {/* Blog Posts Section - Auth Only */}
        {authPosts.length > 0 && (
          <section>
            <SectionHeader 
              title="Members-Only Posts" 
              description="Exclusive content for authenticated members"
              link={{ href: '/blog', text: 'View all posts' }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {authPosts.map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>
          </section>
        )}

        {/* Newsletters Section - Public */}
        <section>
          <SectionHeader 
            title="Newsletters" 
            description="Subscribe to email updates"
            link={{ href: '/newsletters', text: 'Browse all' }}
          />
          {publicNewsletters.length > 0 || authNewsletters.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publicNewsletters.map((newsletter) => (
                <NewsletterCard key={newsletter.id} newsletter={newsletter} />
              ))}
              {authNewsletters.map((newsletter) => (
                <NewsletterCard key={newsletter.id} newsletter={newsletter} />
              ))}
            </div>
          ) : (
            <EmptyState message="No newsletters available yet." />
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
